import { useState } from 'react';

import type { BrandMediaItem } from '../api/brandMedia.api';
import { useCreateBrandMedia } from '../api/mutations';

/** What a picker will accept. The server takes anything; this is what the gallery can draw. */
export const UPLOADABLE = 'image/png,image/jpeg,image/webp,video/mp4';

/** The stills of {@link UPLOADABLE}, for a surface whose result is drawn by an `img`. */
export const UPLOADABLE_STILLS = 'image/png,image/jpeg,image/webp';

/** Same list as {@link UPLOADABLE_STILLS}, shaped for `react-dropzone`'s `accept` map. */
export const MEDIA_ACCEPTED_STILL_TYPES: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

/** Same list as {@link UPLOADABLE}, shaped for `react-dropzone`'s `accept` map instead of a native input's `accept` string. */
export const MEDIA_ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  ...MEDIA_ACCEPTED_STILL_TYPES,
  'video/mp4': ['.mp4'],
};

/** Whether {@link useMediaUpload} files this as footage rather than as a picture. */
export const isClipFile = (file: File): boolean => file.type.startsWith('video/');

/** Where an upload is filed and what it is filed as, stated by the surface it was started from. */
export interface UploadTarget {
  folderId?: string;
  /**
   * Tags every file in the run is created with.
   *
   * An image layer passes the tags its own rule selects on, so what somebody uploads onto a layer
   * appears in that layer rather than only in the folder underneath it. Ingestion fills only the
   * keys a row leaves empty, so these are not overwritten when the row is catalogued.
   */
  tags?: Record<string, string[]>;
}

export interface MediaUpload {
  /**
   * Returns nothing on purpose.
   *
   * The hook already reports `isUploading` and `failure`, so a caller has nothing to do with a
   * promise except forget to await it. Handing one to a DOM handler is also what
   * `no-misused-promises` exists to stop.
   */
  upload: (files: readonly File[], target?: UploadTarget) => void;
  isUploading: boolean;
  /** The last failure, already worded for a person. Null once an upload succeeds. */
  failure: string | null;
  dismissFailure: () => void;
}

/** What this needs of an asset uploader, stated structurally rather than imported. */
export interface AssetUploader {
  mutateAsync: (file: File) => Promise<{ id: string }>;
  isPending: boolean;
}

/**
 * Puts a file in the library, in the two steps that actually take.
 *
 * The bytes go to assets-api and the row goes to guidelines-api, and they are separate services, so
 * this is where the two are held together.
 *
 * The asset uploader is passed in rather than imported. Entities may only reference each other for
 * types, and reaching for `useUploadAsset` here would be a runtime dependency from one entity to
 * another -- so the caller, which is allowed to know both, supplies it. It also means the two-step
 * can be tested without a network client anywhere near it.
 *
 * Files are uploaded one at a time rather than at once. A person picking twenty photographs would
 * otherwise open twenty parallel uploads and twenty ingestion rows, and the failure that produces is
 * a rate limit rather than anything they can act on.
 *
 * A failure stops the run. The files already added stay added, which is why the message says what
 * failed rather than implying nothing happened.
 *
 * `onUploaded` fires per file as each row lands, for a surface that has to act on the picture
 * rather than just list it - the cover picker selects what was just uploaded. It is per file and
 * not per batch because a failure halfway through still added everything before it.
 */
export function useMediaUpload(
  uploadAsset: AssetUploader,
  { onUploaded }: { onUploaded?: (row: BrandMediaItem) => void } = {},
): MediaUpload {
  const createMedia = useCreateBrandMedia();
  const [failure, setFailure] = useState<string | null>(null);

  const run = async (files: readonly File[], target: UploadTarget = {}): Promise<void> => {
    setFailure(null);

    for (const file of files) {
      try {
        const asset = await uploadAsset.mutateAsync(file);
        const row = await createMedia.mutateAsync({
          assetId: asset.id,
          filename: file.name,
          // What the file is, not just what it is called. Without this the server files a clip as a
          // photograph, and a photograph is drawn by an `img` element, which renders nothing for an
          // mp4. The server derives `mediaType` from the mime on its own; sending both means a row
          // is right even when the browser reports no mime at all.
          mimeType: file.type,
          mediaType: isClipFile(file) ? 'video' : 'image',
          // The filename until somebody renames it. A title nobody chose is still better than an
          // empty one: it is what they will recognise the row by in the gallery.
          title: file.name.replace(/\.[^.]+$/, ''),
          // Files it on the way in, so an upload started inside a folder lands there rather than at
          // the top for somebody to move afterwards.
          ...(target.folderId !== undefined ? { folderId: target.folderId } : {}),
          // Stated on create rather than set afterwards: a second request would leave a window in
          // which the row exists and the rule that asked for it does not admit it, so the picture
          // would appear in the gallery it was uploaded from only on a later fetch.
          ...(target.tags !== undefined ? { tags: target.tags } : {}),
        });
        onUploaded?.(row);
      } catch (error) {
        setFailure(error instanceof Error ? error.message : String(error));
        return;
      }
    }
  };

  return {
    upload: (files, target) => void run(files, target),
    isUploading: uploadAsset.isPending || createMedia.isPending,
    failure,
    dismissFailure: () => setFailure(null),
  };
}
